import { useState, useCallback, useEffect } from 'react'
import { ServerExperiments } from '@/experimentation'
import { useScrollToElement } from '@/hooks/useScrollToElement'
import { useToggle } from '@/hooks/useToggle'
import { PageGiveawayContent } from '@/services/GiveawayService/GiveawayService'
import { PhaseSlugForTracking } from '@/services/ProjectPhaseService'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { PageGiveawayTabsItem } from '@/types/codegen-contentful'
import { SweepstakeObject } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

interface UseGiveawayStateParams {
  content: PageGiveawayContent
  theatricalSlug: string
  theatricalTitle: string | null
  tickets: ShopifyProductWithBundles[] | null
  sweepstake: SweepstakeObject
  phase: PhaseSlugForTracking
  experiments?: ServerExperiments
  releaseDate: string | null
}

export const useGiveawayState = ({
  content,
  theatricalSlug,
  theatricalTitle,
  tickets,
  sweepstake,
  phase,
  experiments,
  releaseDate,
}: UseGiveawayStateParams) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isBackgroundVideoPaused, togglePauseBackgroundVideo] = useToggle(false)
  const [isShareModalOpen, setIsShareModalOpen] = useToggle(false)
  const [isScenePlayerOpen, setIsScenePlayerOpen] = useState(false)

  const { push, query } = useSafeRouter()

  const { ref: tabsContainerRef, scrollToElement: scrollToTopTabs } = useScrollToElement<HTMLDivElement>()
  const { ref: mobileTicketContainerRef, scrollToElement: scrollToMobileElement } = useScrollToElement<HTMLDivElement>()
  const { ref: desktopTicketContainerRef, scrollToElement: scrollToDesktopElement } =
    useScrollToElement<HTMLDivElement>()
  const { ref: heroContainerRef } = useScrollToElement<HTMLDivElement>()

  const { ref: detailsContainerRef, scrollToElement: scrollToDetailsElement } = useScrollToElement<HTMLDivElement>()
  const { ref: prizesContainerRef } = useScrollToElement<HTMLDivElement>()
  const { ref: faqContainerRef, scrollToElement: scrollToFaqElement } = useScrollToElement<HTMLDivElement>()

  const isActive = sweepstake.status === 'LIVE'
  const entryVariantId = sweepstake.entryVariantId ?? ''
  const sceneId = getItemOrFirstEntry(query?.sceneId)
  const projectName = sweepstake.projectName

  useEffect(() => {
    if (query.share) {
      setIsShareModalOpen(true)
    }
  }, [query.share, setIsShareModalOpen])

  useEffect(() => {
    if (sceneId) {
      setIsScenePlayerOpen(true)
      togglePauseBackgroundVideo(true)
    }
  }, [sceneId, togglePauseBackgroundVideo])

  // Action handlers
  const scrollToProducts = useCallback(() => {
    scrollToMobileElement()
    scrollToDesktopElement()
  }, [scrollToDesktopElement, scrollToMobileElement])

  const scrollToDetails = useCallback(() => {
    scrollToDetailsElement()
  }, [scrollToDetailsElement])

  const scrollToFaq = useCallback(() => {
    scrollToFaqElement()
  }, [scrollToFaqElement])

  const scrollToTab = useCallback(
    (tabType: PageGiveawayTabsItem['__typename']) => {
      const nextTabIndex = content.tabsCollection?.items?.findIndex((tab) => tab?.__typename === tabType)
      if (nextTabIndex == null) return
      setSelectedIndex(nextTabIndex)
      scrollToTopTabs()
    },
    [content.tabsCollection?.items, scrollToTopTabs],
  )

  const scrollToPrizes = useCallback(() => {
    scrollToTab('GiveawayPrizesTab')
  }, [scrollToTab])

  const handleRefundInfoClick = useCallback(() => {
    scrollToTab('GiveawayFaqTab')
    push('#refundPolicy')
  }, [push, scrollToTab])

  return {
    // State
    content,
    selectedIndex,
    setSelectedIndex,
    isBackgroundVideoPaused,
    togglePauseBackgroundVideo,
    isShareModalOpen,
    setIsShareModalOpen,
    isScenePlayerOpen,
    setIsScenePlayerOpen,

    // Data
    theatricalSlug,
    theatricalTitle,
    tickets,
    sweepstake,
    phase,
    projectName,
    experiments,
    releaseDate,

    // Computed values
    isActive,
    entryVariantId,

    // Actions
    scrollToProducts,
    scrollToPrizes,
    scrollToDetails,
    scrollToFaq,
    scrollToTab,
    handleRefundInfoClick,

    // Refs
    tabsContainerRef,
    mobileTicketContainerRef,
    desktopTicketContainerRef,
    heroContainerRef,
    detailsContainerRef,
    prizesContainerRef,
    faqContainerRef,
  }
}
